import moment from 'moment';
import { getCurrencyAbbreviation } from 'country-currency-map';
import { allCountries } from 'country-telephone-data';

/**
 * @function getAmountFromPercent
 * @params percent, expectedCost
 * @description
 */
export const getAmountFromPercent = (percent, expectedCost) => {
    if (!percent) return 0;
    const percentageAmount = (expectedCost * percent) / 100;
    return parseFloat(percentageAmount).toFixed(2);
};

/**
 * @function getCurrencyText
 * @params country
 * @description used to get currency
 */
export const getCurrencyText = (country) => {
    return getCurrencyAbbreviation(country) || 'MYR';
};

/**
 * @function getTaxTypeFromCountry
 * @params country
 * @description used to get taxt type according to country
 */
export const getTaxTypeFromCountry = (country) => {
    switch (country) {
        case 'Malaysia':
            return 'SST';
        default:
            return 'GST';
    }
};

/**
 * @function getCountryCode
 * @params country
 * @description used to get country code
 */
export const getCountryCode = (country) => {
    return `+${allCountries?.find((c) => c?.name === country)?.dialCode || 60}`;
};

/**
 * @function formatNumber
 * @params value
 * @description used to format number
 */
export const formatNumber = (value) => {
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < Number.EPSILON * 100) {
        return rounded;
    }
    return value?.toFixed(2);
};

/**
 * @function getFeeFromBookingAmount
 * @params fee, bookingAmount
 * @description
 */
export const getFeeFromBookingAmount = (fee, bookingAmount) => {
    // return (Number(fee) / Number(bookingAmount)) * 100;
    return formatNumber((Number(fee) / Number(bookingAmount)) * 100);
};

/**
 * @function getPlatformFeeTypeWithAmount
 * @params platform fee with type
 * @description
 */
export const getPlatformFeeTypeWithAmount = (info, bookingAmount) => {
    let feeType = '';
    // 2 is percentage
    if (info?.platform_fee_type === 2) {
        const percentageSum = formatNumber((Number(info?.platform_fee) / Number(bookingAmount)) * 100);
        feeType = `(${percentageSum}%)`;
        return feeType;
    }
    return feeType;
};

/**
 * @function getPlatformFeeAmount
 * @params platform fee
 * @description
 */
export const getPlatformFeeAmount = (info, bookingAmount) => {
    let platformFee = parseFloat(info?.platform_fee) || 0;

    if (info?.platform_fee_type === 2) {
        const percentageSum = formatNumber((Number(info?.platform_fee) / Number(bookingAmount)) * 100);
        // If Deposit amount
        if (info?.deposit_amount > 0) {
            platformFee = Number(formatNumber((Number(percentageSum) * Number(info?.deposit_amount)) / 100));
            return platformFee.toFixed(2);
        }
        return Number(formatNumber((Number(percentageSum) * Number(bookingAmount)) / 100)).toFixed(2);
    }

    return platformFee.toFixed(2);
};

/**
 * @function getMerchantFeeFromFeeAndBookingAmount
 * @params info, bookingAmount
 * @description
 */
export const getMerchantFeeFromFeeAndBookingAmount = (info, bookingAmount) => {
    let merchantFee = parseFloat(info?.merchant_fees) || 0;
    const feePercentage = getFeeFromBookingAmount(info?.merchant_fees, bookingAmount) / 100;
    // If Deposit
    if (info?.deposit_amount > 0) {
        merchantFee = parseFloat(Number(info?.deposit_amount) * feePercentage);
    }

    return merchantFee.toFixed(2);
};

/**
 * @function getMerchantGstFromFeeAndBookingAmount
 * @params info, bookingAmount
 * @description
 */
export const getMerchantGstFromFeeAndBookingAmount = (info, bookingAmount) => {
    let merchantGst = info?.merchant_gst || 0;
    const feePercentage = getFeeFromBookingAmount(info?.merchant_gst, bookingAmount) / 100;
    // If Deposit
    if (info?.deposit_amount > 0) {
        merchantGst = parseFloat(Number(info?.deposit_amount) * feePercentage);
    }

    return merchantGst.toFixed(2);
};

/**
 * @function getTotalAmount
 * @params calculate total amount
 * @description
 */
export const getTotalAmount = (info, bookingAmount, country) => {
    let totalBookingAMount = 0;
    // Deposit Booking

    if (info?.deposit_amount > 0) {
        totalBookingAMount =
            Number(info?.deposit_amount) +
            Number(getPlatformFeeAmount(info, bookingAmount)) +
            Number(getMerchantFeeFromFeeAndBookingAmount(info, bookingAmount)) +
            Number(getMerchantGstFromFeeAndBookingAmount(info, bookingAmount));
    } else {
        totalBookingAMount =
            Number(bookingAmount) +
            Number(getPlatformFeeAmount(info, bookingAmount)) +
            Number(getMerchantFeeFromFeeAndBookingAmount(info, bookingAmount)) +
            Number(getMerchantGstFromFeeAndBookingAmount(info, bookingAmount));
    }

    return `${getCurrencyText(country)}${totalBookingAMount.toFixed(2)}`;
};

/**
 * @function calculateEndTime
 * @params startTime, hours
 * @description
 */
export const calculateEndTime = (startTime, hours) => {
    const endTime = moment(startTime).add('hours', hours);
    return endTime;
};

/**
 * @function pluralize
 * @params count, noun, suffix
 * @description
 */
const pluralize = (count, noun, suffix = 's') => {
    return `${count} ${noun}${Number(count) !== 1 ? suffix : ''}`;
};

/**
 * @function pricelist
 * @params price, day, currency
 * @description
 */
const pricelist = (price, day, currency) => {
    return price?.map((item) => {
        const d = moment(item?.start_time, 'HH:mm');
        return `${day} : ${item?.start_time} - ${d
            .add(item?.no_of_hours, 'h')
            .format('HH:mm')}${` (Price: ${currency}`}${item?.default?.H1 ? item?.default?.H1 : item?.default}/hr)${
            item?.min_booking_hour ? ` (min: ${pluralize(item?.min_booking_hour, 'hr')}` : ''
        })`;
    });
};

/**
 * @function days
 * @params item
 * @description
 */
const days = (item) => {
    const dayOfWeek = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'Public Holiday',
    ];

    return dayOfWeek[item];
};

/**
 * @function Pricing
 * @params timings, currency
 * @description
 */
const Pricing = (timings, currency) => {

    return timings?.map((item, index) => {

        const d =
            item?.days.length < 2
                ? days(item?.days[0])
                : `${days(item?.days[0])} to ${days(item?.days[item?.days?.length - 1])}`;

        return pricelist(item?.prices, d, currency);
    });
};

/**
 * @function PriceListing
 * @params timings, currency
 * @description
 */
export const PriceListing = (timings, currency) => {

    const pricingLi = Pricing(timings, currency);
    if (pricingLi) {
        const pricenow = pricingLi.flat();
        return [...new Set(pricenow)];
    }
    return [];
};